<script lang="ts" context="module">
  // keep track of what market id is being shown so that only one market is shown at a time
  const marketIdShown = writable('');
</script>

<script lang="ts">
  import { browser } from '$app/environment';
  import type { BetfairMarket, LayoutPreference } from '$types/races';
  import { isSubscribed } from '$utils/auth';
  import { formatTotalMatched } from '$utils/prices';
  import { currentTime } from '$utils/time';
  import { activeSiteConfig } from '$utils/ui';
  import { createFloatingActions } from 'svelte-floating-ui';
  import { flip, offset, shift } from 'svelte-floating-ui/core';
  import { writable, type Readable } from 'svelte/store';

  export let layoutPreference: LayoutPreference;
  export let marketStore: Readable<BetfairMarket> | undefined;

  const [floatingRef, floatingContent] = createFloatingActions({
    strategy: 'absolute',
    placement: 'right',

    middleware: [offset(-3), flip(), shift()]
  });

  function getBetfairLevel() {
    switch (layoutPreference?.level) {
      case 'ZERO':
        return layoutPreference.side === 'BACK' ? 0 : 0;
      case 'ONE':
        return 1;
      case 'TWO':
        return layoutPreference.side === 'BACK' ? 2 : 2;
      default:
        return layoutPreference?.side === 'BACK' ? 0 : 0;
    }
  }

  // calculate a weight average price
  $: weightedAverage = () => {
    const side =
      $marketStore?.[layoutPreference?.side === 'BACK' ? 'back' : 'lay'];
    const sizeSum =
      side?.reduce((acc, curr) => {
        return acc + (curr?.size ?? 0);
      }, 0) ?? 0;
    const weightedSum =
      side?.reduce((acc, curr) => {
        return acc + (curr?.size ?? 0) * (curr?.price ?? 0);
      }, 0) ?? 0;

    const mostRecentUpdated = side?.sort(
      (a, b) =>
        (a?.lastUpdated?.getTime() ?? 0) - (b?.lastUpdated?.getTime() ?? 0)
    )[0];

    const price = Math.round((weightedSum / sizeSum) * 100) / 100;

    return {
      price: isNaN(price) ? null : price,
      lastUpdated: mostRecentUpdated?.lastUpdated,
      size: sizeSum
    } as BetfairMarket['back'][0];
  };

  $: calculated =
    layoutPreference.level === 'WEIGHTED_AVERAGE'
      ? weightedAverage()
      : $marketStore?.[layoutPreference?.side === 'BACK' ? 'back' : 'lay']?.[
          getBetfairLevel()
        ];

  // calculate the price with the betfair reduction factor applied
  // reduction is only applied to any winnings, not the stake
  // so if the price is 1.69, and the reduction factor is 7%
  // then the calculation is (1.69 - 1) * (1 - 0.07) + 1
  $: calculatedPrice =
    $isSubscribed &&
    $activeSiteConfig.betfairReductionFactor &&
    calculated &&
    calculated?.price
      ? Math.round(
          ((calculated.price - 1) *
            ((100 - $activeSiteConfig.betfairReductionFactor) / 100) +
            1) *
            100
        ) / 100
      : calculated?.price ?? 0;

  $: updatedRecentlyClass = (v: Date | undefined) => {
    if (!v) return '';
    if (!browser) return ''; // time wont be accurate on server
    if ($currentTime.valueOf() - v.valueOf() < 100) {
      return 'bg-yellow-300/60';
    } else {
      return '';
    }
  };

  let sideClass = '';
  $: {
    if ($activeSiteConfig.alternateColourMode) {
      if (layoutPreference.side === 'BACK') {
        sideClass = 'bg-blue-300 hover:bg-blue-200';
      } else {
        sideClass = 'bg-rose-100 hover:bg-rose-50';
      }
    } else {
      if (layoutPreference.side === 'BACK') {
        sideClass = 'bg-blue-200 hover:bg-blue-100';
      } else {
        sideClass = 'bg-rose-200 hover:bg-rose-100';
      }
    }
  }

  $: levelClass =
    layoutPreference?.level === 'ONE'
      ? 'bg-opacity-60'
      : layoutPreference?.level === 'TWO'
        ? 'bg-opacity-40'
        : '';

  $: cellClass =
    sideClass +
    ' ' +
    levelClass +
    ($isSubscribed ? '' : 'text-[13px]') +
    (updatedRecentlyClass(calculated?.lastUpdated) ?? '');

  let showDialogTimeout: ReturnType<typeof setTimeout>;
  let loadingTimeout: ReturnType<typeof setTimeout>;

  let showDialog = false;
  $: $marketIdShown !== $marketStore?.id && handleMouseLeave(); // hide if another market is shown
  async function handleMouseLeave() {
    clearTimeout(showDialogTimeout);
    clearTimeout(loadingTimeout);
    showDialog = false;
    loading = false;
  }

  let loading = false;
  let fetchMarketData: () => Promise<boolean>;
  async function handleMouseEnter() {
    marketIdShown.set($marketStore?.id ?? '');
    showDialogTimeout = setTimeout(async () => {
      if (!$marketStore?.id) return;
      loading = true;
      loadingTimeout = setTimeout(() => {
        showDialog = true;
      }, 200);
      await fetchMarketData?.();
      loading = false;
      showDialog = true;
    }, 500);
  }
</script>

<td
  on:mouseenter={handleMouseEnter}
  on:mouseleave={handleMouseLeave}
  use:floatingRef
  class="table-cell cursor-pointer select-none whitespace-nowrap border text-center text-xs"
>
  {#if layoutPreference.side === 'BACK'}
    <div
      class="flex h-8 w-12 cursor-pointer flex-col place-content-center rounded-sm px-1 text-center {cellClass}"
    >
      <span
        class=" {layoutPreference?.level === 'WEIGHTED_AVERAGE'
          ? 'font-semibold'
          : ''}"
      >
        {calculatedPrice === 0 ? '-' : calculatedPrice ?? '-'}
      </span>
      {#if $isSubscribed}
        <span class="text-[10px] italic text-gray-600">
          <span class="">$</span><span>
            {calculated?.size ? Math.round(calculated?.size ?? 0) : '-'}</span
          >
        </span>
      {/if}
    </div>
  {:else}
    <div
      class="flex h-8 w-12 cursor-pointer flex-col place-content-center rounded-sm px-1 text-center {cellClass}"
    >
      <span
        class=" {layoutPreference?.level === 'WEIGHTED_AVERAGE'
          ? 'font-semibold'
          : ''}"
      >
        {calculatedPrice === 0 ? '-' : calculatedPrice ?? '-'}
      </span>
      {#if $isSubscribed}
        <span class="text-[10px] italic text-gray-600">
          <span class="">$</span><span>
            {calculated?.size ? Math.round(calculated?.size ?? 0) : '-'}</span
          >
        </span>
      {/if}
    </div>
  {/if}
  {#if showDialog && $isSubscribed}
    <div
      use:floatingContent
      class="absolute z-40 flex max-h-72 flex-col overflow-y-auto rounded-md border bg-white px-4 py-2 font-normal shadow-sm shadow-gray-300"
    >
      <span class="text-[10px] font-semibold">Runner Matched:</span>
      {formatTotalMatched($marketStore?.totalMatched ?? 0)}
      {#if browser && $isSubscribed && $activeSiteConfig.betfairReductionFactor}
        <div class="mt-1 flex flex-col rounded-lg bg-gray-100 p-2 text-[10px]">
          Exchange Price
          <span class="text-xs font-semibold">
            {calculated?.price ?? '-'}
          </span>
          {$activeSiteConfig.betfairReductionFactor}% reduction applied
        </div>
      {/if}
    </div>
  {/if}
</td>
